import React, { useState, useCallback, useLayoutEffect, memo, type ReactElement } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
// @ts-expect-error - TS2614 - Module '"@atlaskit/inline-edit"' has no exported member 'ExtendedFieldProps'. Did you mean to use 'import ExtendedFieldProps from "@atlaskit/inline-edit"' instead?
import InlineEdit, { type ExtendedFieldProps } from '@atlaskit/inline-edit';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { AsyncEditableAttributeModal } from '@atlassian/jira-servicedesk-insight-editable-attribute-modal/src/async.tsx';
import type { AttributeValuesInfo } from '@atlassian/jira-servicedesk-insight-editable-attribute-modal/src/common/types.tsx';
import type { EditableProps } from '@atlassian/jira-servicedesk-insight-editable-attribute-modal/src/ui/types.tsx';
import {
	objectViewInlineEditSwitchFromReadToEditModeExperience,
	objectViewInlineEditSwitchFromEditToReadModeExperience,
} from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import { MULTI_VALUE_ATTRIBUTE_THRESHOLD } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { InsightObjectAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { MultiValueLozenge } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/multi-value-lozenge/index.tsx';
import { useCmdbErrorTranslation } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/validation-error-translation/index.tsx';
import messages from './messages.tsx';

type BaseProps<Value> = {
	isEditable: boolean;
	isEditingByDefault: boolean;
	attributeType: InsightObjectAttributeType;
	renderReadView: () => ReactElement;
	renderEditView: (
		arg1: ExtendedFieldProps<Value> & {
			hasValidationError: boolean;
			onConfirmChange?: (values: string | string[]) => void;
		},
	) => ReactElement | null;
	value: null | string | string[];
	// This value is compared with the current field value to ensure we don't submit network requests when the value hasn't changed
	storedValue: null | string | string[];
	keepEditViewOpenOnBlur: boolean;
	readViewFitContainerWidth: boolean;
	hideActionButtons?: boolean;
	onValidate?: () => string | undefined;
	testId: string;
	onChange: (arg1: Value) => void;
	onResetValue: () => void;
};

type ScalabilityProps =
	| { scalingEnabled?: false; attributeValuesInfo?: undefined }
	| { scalingEnabled: true; attributeValuesInfo: AttributeValuesInfo };

export type Props<Value> = BaseProps<Value> & ScalabilityProps;
export const InlineValidationErrorID =
	'servicedesk-insight-inline-editable-attributes.ui.editable-attribute.error';
export const InlineValidationError = ({ errorMessage }: { errorMessage: string }) => (
	<Container id={InlineValidationErrorID}>
		<Box xcss={iconContainerStyles}>
			<WarningIcon spacing="spacious" label="" color={token('color.icon.warning')} />
		</Box>
		{errorMessage}
	</Container>
);

const hasValueChanged = (previousValue: null | string | Array<string>, currentValue: unknown) =>
	JSON.stringify(previousValue) !== JSON.stringify(currentValue);

export const InlineEditableAttribute = <T,>(props: Props<T>) => {
	const {
		attributeType,
		isEditable,
		value,
		onChange,
		storedValue,
		onValidate,
		renderReadView,
		renderEditView,
		onResetValue,
		hideActionButtons,
		readViewFitContainerWidth,
		keepEditViewOpenOnBlur,
		isEditingByDefault,
		testId,
		attributeValuesInfo = undefined,
		scalingEnabled = false,
	} = props;

	const { formatMessage } = useIntl();
	const isScaledAttribute =
		storedValue && storedValue.length > MULTI_VALUE_ATTRIBUTE_THRESHOLD && scalingEnabled;
	const { showFlag } = useFlagsService();
	const [{ loading, error }, { update, clearError }] = useEditableAttribute(attributeType);
	const { getFirstValidationErrorMessage } = useCmdbErrorTranslation();

	const [isEditing, setEditing] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isEditableAttributeModalOpen, setIsEditableAttributeModalOpen] = useState<boolean>(false);
	const onConfirmChange = useCallback(
		(values: string | string[]) => {
			// We want to capture this regardless it's been made any changes to the value
			// since what we measure is the transition from edit to read mode
			objectViewInlineEditSwitchFromEditToReadModeExperience.start();

			setEditing(false);
			if (hasValueChanged(storedValue, values) === false) {
				return;
			}

			fireUIAnalytics(createAnalyticsEvent({}), 'inlineEdit confirmed', 'updateInsightObject', {
				attributeType: attributeType.type,
				isLabel: attributeType.label,
			});
			const updatedValues = Array.isArray(values) ? values : [values];
			update({
				updatedValues: updatedValues.map((v: string) => ({
					value: v != null ? v : null,
				})),
				onSuccess: () => setIsEditableAttributeModalOpen(false),
				onError: (err) => {
					if (err instanceof ValidationError) {
						// show validation error for modal in an error flag
						if (isEditableAttributeModalOpen) {
							showFlag({
								type: 'error',
								title: messages.errorFlagTitle,
								description: err.errors[0].error,
								messageId:
									'servicedesk-insight-inline-editable-attributes.common.ui.editable-attribute.showFlag.error.ValidationError',
								messageType: 'transactional',
							});
						} else {
							// after optimistically updating we re-open the inline edit showing inline validation error message whenever we receive a validation error from the requested update
							setEditing(true);
						}
					} else {
						showFlag({
							type: 'error',
							title: messages.errorFlagTitle,
							description: messages.errorFlagDescription,
							messageId:
								'servicedesk-insight-inline-editable-attributes.common.ui.editable-attribute.showFlag.error',
							messageType: 'transactional',
						});
						onResetValue();
					}
				},
			});
		},
		[
			storedValue,
			createAnalyticsEvent,
			attributeType.type,
			attributeType.label,
			update,
			isEditableAttributeModalOpen,
			showFlag,
			onResetValue,
		],
	);

	const onValueChange = useCallback(
		(val: T) => {
			if (error !== undefined) {
				clearError();
			}
			onChange(val);
		},
		[clearError, error, onChange],
	);

	const getIsDirtyState = () => {
		let storedValues: string[] = [];

		let currentValue: string[] = [];
		if (storedValue) {
			storedValues = Array.isArray(storedValue) ? storedValue : [storedValue];
		}

		if (value) {
			currentValue = Array.isArray(value) ? value : [value];
		}

		return hasValueChanged(storedValues.sort(), currentValue.sort());
	};

	if (isScaledAttribute && attributeValuesInfo && !isEditing) {
		const editableProps: EditableProps = isEditable
			? {
					isEditable,
					isSaving: loading,
					renderPicker: () =>
						renderEditView({
							onChange: onValueChange,
							shouldInputRenderValue: false,
							isClearable: false,
						}),
					isDirty: getIsDirtyState(),
					onValueChange,
					storedValue,
					onSave: () => {
						onConfirmChange(value || []);
					},
				}
			: { isEditable };

		return (
			<ObjectDetailsSpacer>
				<ReadOnlyViewContainer>
					<MultiValueLozenge
						attributeValueType={attributeValuesInfo.type}
						count={storedValue.length}
						onClick={() => {
							setIsEditableAttributeModalOpen(true);
						}}
					/>
				</ReadOnlyViewContainer>
				<AsyncEditableAttributeModal
					isOpen={isEditableAttributeModalOpen}
					onClose={() => {
						setIsEditableAttributeModalOpen(false);
						onResetValue();
					}}
					attributeName={attributeType.name}
					attributeValuesInfo={attributeValuesInfo}
					{...editableProps}
				/>
			</ObjectDetailsSpacer>
		);
	}
	if (isEditable === false) {
		return (
			<ReadOnlyViewContainer data-testid={`${testId}.read-view`}>
				{renderReadView()}
			</ReadOnlyViewContainer>
		);
	}

	const experienceSuccessMetaData = {
		attributeTypeConfig: {
			...pick(attributeType, [
				'type',
				'defaultType',
				'additionalValue',
				'indexed',
				'includeChildObjectTypes',
				'typeValueMulti',
				'minimumCardinality',
				'maximumCardinality',
			]),
		},
	};

	// FIXME: Falcons - fix this by extracting memoed component
	// eslint-disable-next-line react/no-unstable-nested-components
	const ReadViewExperienceSuccess = memo(() => {
		useLayoutEffect(() => {
			objectViewInlineEditSwitchFromEditToReadModeExperience.success({
				// @ts-expect-error - TS2322: Type 'string[]' is not assignable to type 'CustomData'. Index signature for type 'string' is missing in type 'string[]'.
				metadata: experienceSuccessMetaData,
			});
		}, []);
		return null;
	});

	const EditViewExperienceSuccess = () => {
		useLayoutEffect(() => {
			objectViewInlineEditSwitchFromReadToEditModeExperience.success({
				// @ts-expect-error - TS2322: Type 'string[]' is not assignable to type 'CustomData'. Index signature for type 'string' is missing in type 'string[]'.
				metadata: experienceSuccessMetaData,
			});
		}, []);
		return null;
	};

	return (
		<InlineEdit
			hideActionButtons={hideActionButtons}
			keepEditViewOpenOnBlur={
				// when an attribute has a validation error we prevent them from re-submitting on blur
				// this prevents a rare edge case of quickly submitting another attribute which could also get a validation error
				// with the user getting in a cycle of submitting onBlur and getting errors ping ponging between attributes
				keepEditViewOpenOnBlur || (error !== undefined && error instanceof ValidationError)
			}
			readViewFitContainerWidth={readViewFitContainerWidth}
			defaultValue={value}
			// some attributes (e.g the status picker) require custom designs that doesn't fit into the standard inline edit read -> edit view transitions
			// in those cases we provide an option to keep the editable attribute component in edit mode to provide an escape hatch for attributes that need extra flexibility
			isEditing={isEditing || isEditingByDefault}
			readView={() => (
				<>
					<div data-testid={`${testId}.read-view`}>{renderReadView()}</div>
					<ReadViewExperienceSuccess />
				</>
			)}
			editButtonLabel={formatMessage(messages.editButtonLabel, {
				attributeTypeName: attributeType.name,
			})}
			editView={(fieldProps: ExtendedFieldProps<unknown>) => {
				const validationError =
					// eslint-disable-next-line no-nested-ternary
					fieldProps.errorMessage != null
						? fieldProps.errorMessage
						: error && error instanceof ValidationError
							? getFirstValidationErrorMessage(error)
							: undefined;

				const hasError = !!validationError;
				return (
					<>
						<div data-testid={`${testId}.edit-view`}>
							<InlineDialog
								placement="right"
								messageId="servicedesk-insight-inline-editable-attributes.common.ui.editable-attribute.inline-dialog"
								messageType="transactional"
								isOpen={isEditing && hasError}
								content={
									validationError !== undefined && (
										<InlineValidationError errorMessage={validationError} />
									)
								}
							>
								{renderEditView({
									...fieldProps,
									onConfirmChange,
									onChange: onValueChange,
									hasValidationError: hasError,
								})}
							</InlineDialog>
						</div>
						<EditViewExperienceSuccess />
					</>
				);
			}}
			onConfirm={onConfirmChange}
			validate={onValidate}
			onCancel={() => {
				objectViewInlineEditSwitchFromEditToReadModeExperience.start();
				if (error !== undefined) {
					clearError();
				}
				setEditing(false);
				onResetValue();
			}}
			onEdit={() => {
				objectViewInlineEditSwitchFromReadToEditModeExperience.start();
				setEditing(true);
			}}
		/>
	);
};

InlineEditableAttribute.defaultProps = {
	// defaults to null as some attributes call onConfirm change directly exposed through props passed to renderEditView
	value: null,
	storedValue: null,
	hideActionButtons: true,
	readViewFitContainerWidth: true,
	isEditingByDefault: false,
	keepEditViewOpenOnBlur: false,
	onChange: noop,
	testId: 'servicedesk-insight-inline-editable-attributes.common.ui.editable-attribute',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const iconContainerStyles = xcss({
	marginRight: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadOnlyViewContainer = styled.div({
	// Offset for atlaskit inline edit read mode view content margin applied
	marginTop: token('space.100'),
});
